// src/pages/groups/groupsShared.jsx
// ستايلات + hooks + مكونات صغيرة مشتركة بين صفحات المجموعات.
// بيستخدم نفس متغيرات الـ CSS اللي حاقنها DashboardLayout (--glass-bg, --accent, ...)
// فبيشتغل مع الـ light/dark تلقائي.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const GROUP_STYLES = `
  :root {
    --gp-ok: #10b981; --gp-warn: #f59e0b; --gp-bad: #ef4444;
    --gp-line: rgba(128,140,170,.22); --gp-soft: rgba(128,140,170,.12);
  }
  .gp-root, .gp-overlay { direction: rtl; font-family: 'Sora','Tajawal',system-ui,sans-serif; color: var(--text-primary); }
  .gp-root { max-width: 1180px; margin: 0 auto; }
  .gp-root *, .gp-overlay * { box-sizing: border-box; }
  .gp-root :focus-visible, .gp-overlay :focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

  .gp-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:22px; }
  .gp-title { font-size:22px; font-weight:700; line-height:1.4; display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  .gp-sub { color:var(--text-secondary); font-size:13px; margin-top:5px; max-width:62ch; line-height:1.8; }
  .gp-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
  .gp-panel { background:var(--glass-bg); border:1px solid var(--glass-border); border-radius:16px;
    backdrop-filter:blur(18px) saturate(160%); -webkit-backdrop-filter:blur(18px) saturate(160%); }

  .gp-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 15px; border-radius:11px;
    border:1px solid var(--gp-line); background:var(--glass-bg); color:var(--text-primary);
    font:inherit; font-size:13px; font-weight:500; cursor:pointer; text-decoration:none; transition:background .2s,border-color .2s; }
  .gp-btn:hover { background:var(--glass-hover); }
  .gp-btn:disabled { opacity:.55; cursor:not-allowed; }
  .gp-btn-primary { background:linear-gradient(135deg,var(--accent),var(--accent-2)); border-color:transparent; color:#fff; box-shadow:0 4px 16px var(--accent-glow); }
  .gp-btn-primary:hover { background:linear-gradient(135deg,var(--accent),var(--accent-2)); filter:brightness(1.07); }
  .gp-btn-danger { color:var(--danger); border-color:rgba(239,68,68,.3); }
  .gp-btn-danger:hover { background:var(--danger-bg); }
  .gp-btn-sm { padding:6px 11px; font-size:12px; border-radius:9px; }
  .gp-icon-btn { display:inline-flex; align-items:center; justify-content:center; width:30px; height:30px; border-radius:9px;
    border:1px solid transparent; background:transparent; color:var(--text-secondary); cursor:pointer; }
  .gp-icon-btn:hover { background:var(--gp-soft); color:var(--text-primary); }
  .gp-icon-btn.danger:hover { background:var(--danger-bg); color:var(--danger); }

  .gp-input, .gp-select, .gp-textarea { width:100%; padding:10px 13px; border-radius:11px; border:1px solid var(--gp-line);
    background:var(--glass-bg); color:var(--text-primary); font:inherit; font-size:13.5px; }
  .gp-textarea { min-height:90px; resize:vertical; line-height:1.7; }
  .gp-input::placeholder, .gp-textarea::placeholder { color:var(--text-secondary); opacity:.8; }
  .gp-select { width:auto; min-width:140px; }
  .gp-select option { color:#1a2035; background:#fff; }
  .dark .gp-select option { color:#e8eeff; background:#12182c; }
  .gp-search { position:relative; flex:1; min-width:200px; }
  .gp-search svg { position:absolute; top:50%; inset-inline-start:12px; transform:translateY(-50%); color:var(--text-secondary); pointer-events:none; }
  .gp-search .gp-input { padding-inline-start:38px; }
  .gp-toolbar { display:flex; gap:10px; flex-wrap:wrap; align-items:center; margin-bottom:16px; }
  .gp-field { display:flex; flex-direction:column; gap:6px; margin-bottom:14px; }
  .gp-field label { font-size:12.5px; font-weight:600; color:var(--text-secondary); }
  .gp-check { display:flex; align-items:center; gap:8px; font-size:13px; cursor:pointer; }
  .gp-check input, .gp-row input[type=checkbox] { width:16px; height:16px; accent-color:var(--accent); cursor:pointer; }

  .gp-seg { display:inline-flex; padding:3px; border-radius:11px; background:var(--gp-soft); }
  .gp-seg button { padding:6px 13px; border:0; border-radius:8px; background:transparent; color:var(--text-secondary); font:inherit; font-size:12.5px; cursor:pointer; }
  .gp-seg button[aria-pressed="true"] { background:var(--nav-active-bg); color:var(--accent); font-weight:600; box-shadow:0 1px 6px rgba(79,124,255,.14); }

  .gp-stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(160px,1fr)); gap:12px; margin-bottom:22px; }
  .gp-stat { padding:15px 17px; }
  .gp-stat b { display:block; font-size:26px; font-weight:700; line-height:1.15; font-variant-numeric:tabular-nums; }
  .gp-stat span { display:block; margin-top:6px; font-size:12.5px; color:var(--text-secondary); }

  .gp-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:16px; }
  .gp-card { padding:18px; display:flex; flex-direction:column; gap:12px; }
  .gp-card h3 { font-size:15.5px; font-weight:600; line-height:1.5; }
  .gp-card p { font-size:12.5px; color:var(--text-secondary); line-height:1.8; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height:3.6em; }
  .gp-card-top { display:flex; justify-content:space-between; gap:10px; align-items:flex-start; }
  .gp-card-foot { display:flex; justify-content:space-between; align-items:center; gap:8px; margin-top:auto; font-size:12.5px; color:var(--text-secondary); }
  .gp-link { color:inherit; text-decoration:none; }
  .gp-link:hover h3 { color:var(--accent); }

  .gp-badge { display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border-radius:99px; font-size:11.5px; font-weight:600; background:var(--gp-soft); color:var(--text-secondary); }
  .gp-badge.ok { background:rgba(16,185,129,.13); color:var(--gp-ok); }

  .gp-bar { display:flex; height:7px; border-radius:99px; overflow:hidden; background:var(--gp-soft); gap:2px; }
  .gp-bar i { display:block; height:100%; }
  .gp-bar i.ok, .gp-dot.ok { background:var(--gp-ok); }
  .gp-bar i.warn, .gp-dot.warn { background:var(--gp-warn); }
  .gp-bar i.none, .gp-dot.none { background:rgba(128,140,170,.5); }
  .gp-dot { display:inline-block; width:8px; height:8px; border-radius:50%; margin-inline-end:6px; }
  .gp-legend { display:flex; gap:14px; flex-wrap:wrap; font-size:12px; color:var(--text-secondary); margin-top:9px; }

  .gp-tabs { display:flex; gap:4px; border-bottom:1px solid var(--gp-line); margin-bottom:18px; overflow-x:auto; }
  .gp-tab { padding:11px 16px; border:0; background:none; color:var(--text-secondary); font:inherit; font-size:13.5px; cursor:pointer;
    border-bottom:2px solid transparent; margin-bottom:-1px; white-space:nowrap; }
  .gp-tab[aria-selected="true"] { color:var(--accent); font-weight:600; border-bottom-color:var(--accent); }

  .gp-table { overflow:hidden; }
  .gp-row { display:grid; grid-template-columns:28px minmax(0,2fr) 90px 90px 130px 72px; gap:12px; align-items:center; padding:12px 16px; border-bottom:1px solid var(--gp-line); }
  .gp-row.no-check { grid-template-columns:minmax(0,2fr) 90px 90px 130px 72px; }
  .gp-row.mine { grid-template-columns:minmax(0,2fr) minmax(0,1.3fr) 90px 130px 48px; }
  .gp-row:last-child { border-bottom:0; }
  .gp-row.head { font-size:12px; color:var(--text-secondary); font-weight:600; padding-block:10px; background:var(--gp-soft); }
  .gp-who { display:flex; align-items:center; gap:11px; min-width:0; }
  .gp-who b { display:block; font-size:13.5px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .gp-who small { display:block; font-size:11.5px; color:var(--text-secondary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .gp-num { font-variant-numeric:tabular-nums; font-size:13.5px; }
  .gp-muted { color:var(--text-secondary); font-size:12.5px; }
  .gp-recency { display:inline-flex; align-items:center; gap:7px; font-size:12.5px; }
  .gp-recency::before { content:""; width:7px; height:7px; border-radius:50%; background:rgba(128,140,170,.55); flex-shrink:0; }
  .gp-recency.ok::before { background:var(--gp-ok); }
  .gp-recency.warn::before { background:var(--gp-warn); }
  .gp-recency.bad::before { background:var(--gp-bad); }
  .gp-row-actions { display:flex; gap:2px; justify-content:flex-end; }
  .gp-tags { display:flex; flex-wrap:wrap; gap:5px; }
  .gp-tags a { padding:2px 9px; border-radius:99px; background:var(--gp-soft); color:var(--text-primary); font-size:11.5px; text-decoration:none; }
  .gp-tags a:hover { background:var(--nav-active-bg); color:var(--accent); }
  @media (max-width:760px) {
    .gp-row { grid-template-columns:28px minmax(0,1fr) 64px; }
    .gp-row.no-check { grid-template-columns:minmax(0,1fr) 64px; }
    .gp-row.mine { grid-template-columns:minmax(0,1fr) 48px; }
    .gp-hide-sm { display:none; }
  }

  .gp-selbar { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:10px 14px; margin-bottom:12px; border-radius:12px;
    background:var(--nav-active-bg); border:1px solid var(--nav-active-border); font-size:13px; }
  .gp-pager { display:flex; justify-content:center; align-items:center; gap:12px; margin-top:18px; font-size:13px; color:var(--text-secondary); }

  .gp-rank { display:flex; align-items:center; gap:14px; padding:12px 16px; border-bottom:1px solid var(--gp-line); }
  .gp-rank:last-child { border-bottom:0; }
  .gp-rank-n { width:30px; height:30px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; font-weight:700; font-size:13px; background:var(--gp-soft); flex-shrink:0; }
  .gp-rank-n.top { background:linear-gradient(135deg,var(--accent),var(--accent-2)); color:#fff; }
  .gp-rank .gp-who { flex:1; }

  .gp-prog { height:6px; border-radius:99px; background:var(--gp-soft); overflow:hidden; }
  .gp-prog i { display:block; height:100%; border-radius:99px; background:linear-gradient(90deg,var(--accent),var(--accent-2)); }
  .gp-avatar { border-radius:50%; object-fit:cover; display:inline-flex; align-items:center; justify-content:center; font-weight:700; flex-shrink:0; }

  .gp-empty { padding:44px 20px; text-align:center; color:var(--text-secondary); font-size:13.5px; line-height:1.9; }
  .gp-empty b { display:block; color:var(--text-primary); font-size:15px; margin-bottom:4px; }
  .gp-error { padding:11px 14px; border-radius:11px; background:var(--danger-bg); color:var(--danger); font-size:13px; margin-bottom:14px; }
  .gp-spin { width:22px; height:22px; border-radius:50%; border:2.5px solid var(--gp-line); border-top-color:var(--accent); animation:gp-rot .8s linear infinite; margin:44px auto; }
  @keyframes gp-rot { to { transform:rotate(360deg); } }

  .gp-overlay { position:fixed; inset:0; z-index:200; background:var(--overlay-bg); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; padding:20px; }
  .gp-overlay.drawer { justify-content:flex-end; align-items:stretch; padding:0; }
  .gp-modal { width:100%; max-height:calc(100vh - 40px); display:flex; flex-direction:column; background:var(--dropdown-bg); border:1px solid var(--glass-border);
    border-radius:18px; box-shadow:0 24px 70px rgba(30,40,90,.25); animation:gp-pop .18s ease; }
  .gp-modal-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:16px 20px; border-bottom:1px solid var(--gp-line); }
  .gp-modal-head h3 { font-size:15.5px; font-weight:600; }
  .gp-modal-body { padding:18px 20px; overflow-y:auto; }
  .gp-modal-foot { display:flex; justify-content:flex-start; gap:8px; padding:14px 20px; border-top:1px solid var(--gp-line); }
  .gp-drawer { width:min(460px,100vw); height:100%; display:flex; flex-direction:column; background:var(--dropdown-bg); border-inline-start:1px solid var(--glass-border); animation:gp-slide .22s ease; }
  .gp-drawer .gp-modal-body { flex:1; }
  @keyframes gp-pop { from { opacity:0; transform:translateY(8px) scale(.98); } to { opacity:1; transform:none; } }
  @keyframes gp-slide { from { opacity:0; transform:translateX(-24px); } to { opacity:1; transform:none; } }
  @media (prefers-reduced-motion:reduce) { .gp-modal, .gp-drawer, .gp-spin { animation:none; } }

  .gp-toast { position:fixed; bottom:24px; left:50%; transform:translateX(-50%); z-index:300; padding:11px 18px; border-radius:12px; font-size:13px; font-weight:500;
    font-family:'Sora','Tajawal',system-ui,sans-serif; background:var(--text-primary); color:var(--bg-base); box-shadow:0 10px 30px rgba(0,0,0,.2); max-width:90vw; }
  .gp-toast.err { background:var(--danger); color:#fff; }

  .gp-pick { display:flex; flex-direction:column; gap:6px; max-height:300px; overflow-y:auto; margin-top:12px; }
  .gp-pick button { display:flex; align-items:center; gap:11px; padding:9px 11px; border-radius:11px; border:1px solid var(--gp-line); background:transparent; color:inherit; font:inherit; cursor:pointer; text-align:start; width:100%; }
  .gp-pick button[aria-pressed="true"] { border-color:var(--accent); background:var(--nav-active-bg); }
  .gp-chips { display:flex; flex-wrap:wrap; gap:6px; margin-top:12px; }
  .gp-chip { display:inline-flex; align-items:center; gap:4px; padding:3px 11px 3px 4px; border-radius:99px; background:var(--nav-active-bg); border:1px solid var(--nav-active-border); font-size:12px; }
  .gp-chip button { display:inline-flex; border:0; background:none; color:var(--text-secondary); cursor:pointer; padding:2px; }

  .gp-levels { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
  .gp-level { padding:9px 11px; border-radius:11px; background:var(--gp-soft); font-size:12px; color:var(--text-secondary); }
  .gp-level b { display:block; margin-top:2px; font-size:13px; color:var(--text-primary); font-weight:600; }
  .gp-sec-title { font-size:13px; font-weight:700; margin:20px 0 10px; }
  .gp-prog-card { border:1px solid var(--gp-line); border-radius:13px; margin-bottom:10px; overflow:hidden; }
  .gp-prog-card > button { width:100%; display:block; padding:13px 14px; border:0; background:none; color:inherit; font:inherit; cursor:pointer; text-align:start; }
  .gp-prog-meta { display:flex; justify-content:space-between; gap:8px; font-size:12px; color:var(--text-secondary); margin:8px 0 6px; }
  .gp-skill-row { padding:10px 14px; border-top:1px solid var(--gp-line); font-size:12.5px; }
  .gp-skill-row .top { display:flex; justify-content:space-between; gap:10px; margin-bottom:6px; }
`;

/* ─── hooks ─────────────────────────────────────────────── */

export function useGroupStyles() {
  useLayoutEffect(() => {
    const id = "gp-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = GROUP_STYLES;
      document.head.appendChild(tag);
    }
  }, []);
}

export function useDebounce(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function useToast() {
  const [t, setT] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const notify = useCallback((message, type = "ok") => {
    clearTimeout(timer.current);
    setT({ message, type });
    timer.current = setTimeout(() => setT(null), 3400);
  }, []);
  const node = t
    ? createPortal(
        <div className={`gp-toast ${t.type === "err" ? "err" : ""}`} role="status">
          {t.message}
        </div>,
        document.body
      )
    : null;
  return [node, notify];
}

/* ─── formatters ────────────────────────────────────────── */

export function daysSince(iso) {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}

export function arDays(n) {
  if (n === 1) return "يوم";
  if (n === 2) return "يومين";
  return n <= 10 ? `${n} أيام` : `${n} يوماً`;
}

export function formatRelative(iso) {
  const d = daysSince(iso);
  if (d === null) return "لم يبدأ بعد";
  if (d === 0) return "اليوم";
  if (d === 1) return "أمس";
  if (d < 60) return `منذ ${arDays(d)}`;
  const m = Math.floor(d / 30);
  if (m >= 12) return "منذ أكثر من سنة";
  return m === 2 ? "منذ شهرين" : `منذ ${m} أشهر`;
}

export function recencyTone(iso) {
  const d = daysSince(iso);
  if (d === null) return "none";
  if (d <= 2) return "ok";
  if (d <= 7) return "warn";
  return "bad";
}

export function formatDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("ar-EG-u-nu-latn", { dateStyle: "medium" }).format(new Date(iso));
}

export const fmt = (n) => Number(n ?? 0).toLocaleString("en-US");

/* ─── UI pieces ─────────────────────────────────────────── */

const hueOf = (s = "?") => {
  let h = 0;
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
};

export function Avatar({ name, src, size = 36 }) {
  if (src) return <img className="gp-avatar" src={src} alt="" style={{ width: size, height: size }} />;
  const initials = (name || "؟").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("");
  const h = hueOf(name);
  return (
    <span
      className="gp-avatar"
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: size * 0.38, background: `hsl(${h} 70% 92%)`, color: `hsl(${h} 55% 32%)` }}
    >
      {initials}
    </span>
  );
}

export function Spinner() {
  return <div className="gp-spin" role="status" aria-label="جاري التحميل" />;
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="gp-empty">
      <b>{title}</b>
      {hint}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

export function Stat({ value, label }) {
  return (
    <div className="gp-panel gp-stat">
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}

export function Seg({ value, onChange, options, label }) {
  return (
    <div className="gp-seg" role="group" aria-label={label}>
      {options.map(([v, text]) => (
        <button key={String(v)} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>
          {text}
        </button>
      ))}
    </div>
  );
}

export function DaysSeg({ value, onChange, options = [7, 14, 30] }) {
  return (
    <Seg
      label="الفترة الزمنية"
      value={value}
      onChange={onChange}
      options={options.map((d) => [d, `آخر ${arDays(d)}`])}
    />
  );
}

export function ProgressBar({ value }) {
  return (
    <div className="gp-prog" role="progressbar" aria-valuenow={Math.round(value || 0)} aria-valuemin={0} aria-valuemax={100}>
      <i style={{ width: `${Math.min(100, Math.max(0, value || 0))}%` }} />
    </div>
  );
}

// segments: [{ tone: 'ok'|'warn'|'none', value, label }]
export function ActivityBar({ segments }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  return (
    <div className="gp-bar" role="img" aria-label={segments.map((s) => `${s.label}: ${s.value}`).join("، ")}>
      {total > 0 &&
        segments
          .filter((s) => s.value > 0)
          .map((s) => <i key={s.tone} className={s.tone} style={{ width: `${(s.value / total) * 100}%` }} />)}
    </div>
  );
}

export function Legend({ segments }) {
  return (
    <div className="gp-legend">
      {segments.map((s) => (
        <span key={s.tone}>
          <i className={`gp-dot ${s.tone}`} />
          {s.label} ({s.value})
        </span>
      ))}
    </div>
  );
}

export function Pagination({ page, totalPages, onChange }) {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <nav className="gp-pager" aria-label="التنقل بين الصفحات">
      <button className="gp-btn gp-btn-sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        <ChevronRight size={14} /> السابق
      </button>
      <span>
        صفحة {page} من {totalPages}
      </span>
      <button className="gp-btn gp-btn-sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        التالي <ChevronLeft size={14} />
      </button>
    </nav>
  );
}

function useEscape(onClose) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);
}

export function Modal({ title, onClose, children, footer, width = 480 }) {
  useEscape(onClose);
  return createPortal(
    <div className="gp-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="gp-modal" style={{ maxWidth: width }} role="dialog" aria-modal="true" aria-label={title}>
        <div className="gp-modal-head">
          <h3>{title}</h3>
          <button className="gp-icon-btn" onClick={onClose} aria-label="إغلاق">
            <X size={16} />
          </button>
        </div>
        <div className="gp-modal-body">{children}</div>
        {footer && <div className="gp-modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

export function Drawer({ title, onClose, children }) {
  useEscape(onClose);
  return createPortal(
    <div className="gp-overlay drawer" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="gp-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="gp-modal-head">
          <h3>{title}</h3>
          <button className="gp-icon-btn" onClick={onClose} aria-label="إغلاق">
            <X size={16} />
          </button>
        </div>
        <div className="gp-modal-body">{children}</div>
      </aside>
    </div>,
    document.body
  );
}

export function ConfirmDialog({ title, message, confirmLabel = "تأكيد", danger, loading, onConfirm, onCancel }) {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      width={420}
      footer={
        <>
          <button className={`gp-btn ${danger ? "gp-btn-danger" : "gp-btn-primary"}`} onClick={onConfirm} disabled={loading}>
            {loading ? "جاري التنفيذ..." : confirmLabel}
          </button>
          <button className="gp-btn" onClick={onCancel} disabled={loading}>
            إلغاء
          </button>
        </>
      }
    >
      <p style={{ fontSize: 13.5, lineHeight: 1.9 }}>{message}</p>
    </Modal>
  );
}